import type { KeyboardEvent, ReactElement } from "react"
import { useCallback, useEffect, useRef } from "react"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import CtaElement from "@gatsby-plugin-generic-page/components/CtaElement"
import Header from "@gatsby-plugin-generic-page/components/Header"
import { getColorMaskStyle } from "@gatsby-plugin-generic-page/fragments/colorMask"
import { ContainerWidthType } from "@gatsby-plugin-generic-page/fragments/containerSettings"
import type { CtaElement as CtaElementType } from "@gatsby-plugin-generic-page/fragments/ctaElement"
import type { FullVideoSliderSectionFlatData } from "@gatsby-plugin-generic-page/fragments/fullVideoSliderSection"
import type { GhostButton } from "@gatsby-plugin-generic-page/fragments/ghostButton"
import type { UDSBackgroundColorComponent } from "@gatsby-plugin-generic-page/fragments/udsBackgroundColor"
import {
  getWrapperHeightVh,
  useFullVideoSliderScroll,
} from "@gatsby-plugin-generic-page/hooks/useFullVideoSliderScroll"
import { SquidexComponentType } from "@gatsby-plugin-generic-page/types/componentType"
import { typeMatches } from "@onestore-graphql"
import { cn } from "~/helpers/cn"
import assetsSquidexToVideoObject from "~/lib/assetsSquidexToVideoObject"
import isEmpty from "~/lib/isEmpty"
import { getFrameStyle, getSlideStyle } from "./geometry"

/** Gap between one button's entrance and the next. */
const BUTTON_STAGGER_MS = 120
/** How long a single button takes to arrive. */
const BUTTON_ENTER_MS = 400
/**
 * Width of a tile that lines up with its neighbours. The same for every one of
 * them, so a long label cannot come out wider than the rest, and it is the
 * width cp-shop gives the same UDS tile.
 *
 * Fixed rather than grown from a smaller basis: with `grow` the tiles share
 * each *line* of a wrapped row, so a line holding fewer of them made those
 * wider than the rest — at 768px five tiles came out 138px and the one that
 * wrapped came out 186px. A fixed basis is the same on every line. Flex still
 * shrinks it if a screen is too narrow for even one.
 *
 * Spelled out in full, and repeated for the `max-md:` variant, because Tailwind
 * scans source text — a class assembled from these at runtime is never
 * generated.
 */
const TILE_SIZING_FROM_MD = "md:basis-[11.625rem]"
/**
 * Below `md` the tiles fill the row instead, growing from a basis narrow enough
 * that two still fit side by side on a small phone. Rows there hold two tiles
 * each, so they come out equal anyway, and filling the width beats leaving a
 * phone's screen half empty for the sake of it.
 */
const TILE_SIZING_BELOW_MD =
  "max-md:grow max-md:basis-[8.5rem] max-md:max-w-[11.625rem]"

// Dark surface used when no background is chosen — the section was always dark
// behind the video, which the retired HEL `color="dark"` used to provide.
const DEFAULT_BACKGROUND_COLOR: UDSBackgroundColorComponent = {
  __typename: "UdsBackgroundColorComponent",
  color: "surface-subtle-invert",
}

/**
 * What a reader can tab to inside a slide. Slides hold CTAs, which render as
 * links or buttons; the slide itself is included as a fallback so a slide with
 * no CTAs is still somewhere focus can land.
 */
const FOCUSABLE_SELECTOR = "a[href], button:not([disabled])"

function getFocusableElements(slide: HTMLElement): HTMLElement[] {
  return Array.from(slide.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
}

/** Whether this button is a ghost tile — a launcher card, not a plain CTA. */
function isGhostTile(button: CtaElementType): boolean {
  return typeMatches(
    button.flatData.ctaType?.__typename,
    SquidexComponentType.GHOST_BUTTON
  )
}

/**
 * Whether this tile shares the row's width and height at every width. Ghost
 * tiles read as a set, so they line up — except one the editor marked compact,
 * which is meant to take less room than the rest. Every other CTA sizes itself,
 * and pulling one up to match a tall neighbour would distort it.
 *
 * On a phone every tile is compact anyway, so none stands out by being compact
 * and they all line up instead — see the `max-md:` rules on the row and the
 * wrapper, which say that in CSS rather than at render time.
 */
function shouldStretchTile(button: CtaElementType): boolean {
  return (
    isGhostTile(button) &&
    (button.flatData.ctaType as GhostButton).isCompact !== true
  )
}

type SlideButtonsProps = {
  buttons: CtaElementType[]
  isActive: boolean
}

/**
 * The slide's buttons, laid out the same way whatever their type, but arriving
 * on their own timing rather than with the header: each one grows from half
 * size and fades in, one after the next.
 *
 * Each button goes through `CtaElement` singly so it can carry its own wrapper
 * to animate — `CtaElement` renders a lone element without its flex scaffolding,
 * so this costs nothing in markup.
 */
function SlideButtons({
  buttons,
  isActive,
}: SlideButtonsProps): ReactElement<SlideButtonsProps> {
  return (
    // `items-stretch` sizes every animation wrapper to the row, but the tile
    // inside keeps its own height — the theme provider and the anchor both have
    // to be told to fill it, and only for the tiles that opted in. Anything
    // without the marker sits at its natural size, top-aligned.
    <div className="flex w-full flex-wrap items-stretch justify-center gap-2 [&>div[data-stretch]>div]:h-full [&>div[data-stretch]>div>a]:h-full max-md:[&>div[data-tile]>div]:h-full max-md:[&>div[data-tile]>div>a]:h-full">
      {buttons.map((button, index) => (
        <div
          key={`slide-button-${button.id}`}
          className={cn(
            // `scale-*` in Tailwind v4 sets the standalone `scale` property, not
            // `transform` — transitioning `transform` here would leave the size
            // snapping instantly while only the fade animated.
            "transition-[opacity,scale] ease-out motion-reduce:transition-none",
            // Tiles that line up share the row equally rather than each sizing
            // to its own label, so a long one cannot come out wider than the
            // rest — an equal basis is what makes the split ignore content
            // width.
            // Every ghost tile lines up below `md`, where they are all compact
            // and so none of them stands out; from `md` up only the ones that
            // are not compact do.
            isGhostTile(button) && TILE_SIZING_BELOW_MD,
            shouldStretchTile(button) && TILE_SIZING_FROM_MD,
            isActive ? "scale-100 opacity-100" : "scale-50 opacity-0"
          )}
          style={{
            transitionDuration: `${BUTTON_ENTER_MS}ms`,
            // Only the arrival is staggered; leaving resets them together, out
            // of sight, so the next visit replays the entrance from the start.
            transitionDelay: isActive
              ? `${index * BUTTON_STAGGER_MS}ms`
              : "0ms",
          }}
          data-tile={isGhostTile(button) ? "" : undefined}
          data-stretch={shouldStretchTile(button) ? "" : undefined}
          data-testid="slide-button"
        >
          <CtaElement ctaElement={[button]} />
        </div>
      ))}
    </div>
  )
}

/**
 * Full-bleed video with a scroll-driven slider on top of it.
 *
 * The section is a tall wrapper with a sticky, viewport-tall stage inside.
 * Scrolling into it sends the video from its container out to the screen edges
 * in one move, each further `SLIDE_VIEWPORTS` of scrolling advances one slide,
 * and the last stretch drops the video back into the container. Neither the expansion
 * nor the slide switch tracks the scroll pixel by pixel — the scroll only picks
 * the state and the animation plays out on its own clock (see
 * `useFullVideoSliderScroll`). While the video covers the screen
 * the wrapper carries `data-suppresses-sticky-header`, which keeps the sticky
 * page header from sliding back in on scroll up — the same treatment comparison
 * tables get (see `gatsby-plugin-onestore/components/Header`).
 */
export default function FullVideoSliderSection({
  video,
  colorMaskEnabled = false,
  colorMask,
  slides,
  backgroundColor,
  containerWidth = ContainerWidthType.DEFAULT,
  id,
}: FullVideoSliderSectionFlatData): ReactElement<FullVideoSliderSectionFlatData> | null {
  const slidesCount = slides?.length ?? 0
  const colorMaskStyle = colorMaskEnabled ? getColorMaskStyle(colorMask) : null
  const { wrapperRef, activeIndex, isFullScreen, goToSlide } =
    useFullVideoSliderScroll(slidesCount)
  const stageRef = useRef<HTMLDivElement>(null)
  /**
   * Which end of the next slide to put focus on, once it arrives. The slide
   * only enters the tab order when it becomes the active one, and that follows
   * the animation rather than the scroll, so focus cannot be moved in the same
   * breath as the jump.
   */
  const pendingFocusRef = useRef<"first" | "last" | null>(null)

  /**
   * Tab moves between slides, not just within one.
   *
   * Only the showing slide is in the tab order, and slides advance on scroll —
   * so without this a reader tabbing through the page reaches the first slide
   * and then leaves the section entirely, with the rest unreachable. At either
   * end of a slide, Tab scrolls to the neighbouring one instead and hands focus
   * on when it arrives. At the section's own ends it does nothing, letting
   * focus carry on out to the rest of the page.
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      const slide = stageRef.current?.querySelector<HTMLElement>(
        `[data-testid="full-video-slide-${activeIndex}"]`
      )

      if (event.key !== "Tab" || !slide) {
        return
      }

      const focusable = getFocusableElements(slide)
      // A slide with nothing to focus holds focus on itself, and is then both
      // its own first and last stop — otherwise Tab strands the reader on it.
      const isAtEnd = focusable.length === 0 && event.target === slide
      const isLeavingForwards =
        !event.shiftKey && (isAtEnd || event.target === focusable.at(-1))
      const isLeavingBackwards =
        event.shiftKey && (isAtEnd || event.target === focusable.at(0))

      if (isLeavingForwards && activeIndex < slidesCount - 1) {
        event.preventDefault()
        pendingFocusRef.current = "first"
        goToSlide(activeIndex + 1)
      } else if (isLeavingBackwards && activeIndex > 0) {
        event.preventDefault()
        pendingFocusRef.current = "last"
        goToSlide(activeIndex - 1)
      }
    },
    [activeIndex, slidesCount, goToSlide]
  )

  useEffect(() => {
    const pendingFocus = pendingFocusRef.current
    const slide = stageRef.current?.querySelector<HTMLElement>(
      `[data-testid="full-video-slide-${activeIndex}"]`
    )

    if (!pendingFocus || !slide) {
      return
    }

    pendingFocusRef.current = null

    const focusable = getFocusableElements(slide)
    // A slide with no CTAs still takes focus itself, so the reader hears its
    // heading rather than being dropped back to the top of the document.
    const target =
      (pendingFocus === "first" ? focusable.at(0) : focusable.at(-1)) ?? slide

    // Without `preventScroll` the browser scrolls the focused element into
    // view, and this section reads its state *from* the scroll — so that scroll
    // moves the slide on again underneath the focus we just placed.
    target.focus({ preventScroll: true })
  }, [activeIndex])

  if (isEmpty(video) || slidesCount === 0) {
    return null
  }

  const { src, webmSrc, movSrc, fileType } = assetsSquidexToVideoObject(
    video[0].flatData.video
  )
  // The video's required fallback image sits under the video as a background
  // layer and doubles as the poster. Between them they cover every way the
  // video can fail to show: `poster` holds the frame while it loads, if the
  // source fails, and when autoplay is refused; the layer behind also covers a
  // video element that renders nothing at all, and gives the first paint an
  // image instead of an empty box under the slide text.
  const { poster: posterAsset, fallbackImage } = video[0].flatData
  const posterSource = !isEmpty(posterAsset) ? posterAsset : fallbackImage
  const poster = !isEmpty(posterSource) ? posterSource[0].url : undefined
  const fallbackImageUrl = !isEmpty(fallbackImage)
    ? fallbackImage[0].url
    : undefined

  return (
    <BaseSection
      id={id}
      customColor={backgroundColor ?? DEFAULT_BACKGROUND_COLOR}
      // The slides' wording sits on the video and its dark mask, never on the
      // section surface, so it stays light whichever surface is chosen.
      onColor="backgroundDark"
      padding={{
        __typename: SquidexComponentType.SPACING,
        top: "sticky",
        bottom: "sticky",
      }}
    >
      <div
        ref={wrapperRef}
        style={{ height: `${getWrapperHeightVh(slidesCount)}vh` }}
        data-suppresses-sticky-header={isFullScreen ? "" : undefined}
        data-testid="full-video-slider"
      >
        <div
          ref={stageRef}
          onKeyDown={handleKeyDown}
          className={cn(
            // The slides are positioned against the stage rather than the frame,
            // which `sticky` already makes a containing block for — adding
            // `relative` would be a second `position` on the same element, and
            // which one won would come down to Tailwind's emitted order.
            "sticky top-0 flex h-screen w-full items-center justify-center overflow-hidden",
            // Slides read their travel distance from here, so a reader who asked
            // for reduced motion gets the fades without the movement.
            "[--full-video-slide-travel:6rem] motion-reduce:[--full-video-slide-travel:0rem]"
          )}
        >
          <div
            className="relative overflow-hidden"
            style={getFrameStyle(containerWidth, fallbackImageUrl)}
          >
            {/*
              Decorative background video — all wording lives in the slides, so
              it is hidden from assistive tech rather than announced as playable
              media a reader might try to reach.
            */}
            <video
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover"
              poster={poster}
              autoPlay
              loop
              muted
              playsInline
              disablePictureInPicture
              preload="metadata"
            >
              {webmSrc ? <source src={webmSrc} type="video/webm" /> : null}
              {movSrc ? <source src={movSrc} type="video/quicktime" /> : null}
              <source
                src={src}
                type={fileType ? `video/${fileType}` : undefined}
              />
            </video>
            {colorMaskStyle ? (
              // Sits above the video but inside the frame, so it expands with
              // it; never intercepts clicks meant for the slides.
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={colorMaskStyle}
                data-testid="full-video-color-mask"
              />
            ) : null}
          </div>

          {slides.map((slide, index) => (
            <div
              // Slides come from a CMS array and have no stable id of their own.
              key={`full-video-slide-${index}`}
              className={cn(
                "absolute inset-0 mx-auto flex max-w-[82rem] flex-col items-center justify-center gap-6 px-4 text-center",
                // Hard guarantee that only one slide is ever on screen, rather
                // than trusting the opacity arithmetic: `activeIndex` flips at
                // offset 0.5, which is exactly where the outgoing slide's fade
                // reaches 0, so nothing visible is ever cut off. `invisible`
                // also takes the slide out of the tab order and the a11y tree.
                index === activeIndex ? null : "invisible pointer-events-none"
              )}
              style={getSlideStyle(index)}
              // Focusable only on purpose, as a landing place for a slide that
              // carries no CTAs of its own.
              tabIndex={-1}
              data-testid={`full-video-slide-${index}`}
            >
              {!isEmpty(slide.header) ? <Header {...slide.header} /> : null}

              {!isEmpty(slide.buttons) ? (
                <SlideButtons
                  buttons={slide.buttons}
                  isActive={index === activeIndex}
                />
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </BaseSection>
  )
}
